/**
 * text 内のインライントークン（`{{name:value}}` と `@userId` 糖衣）を
 * 分割するユーティリティ。レンダリング時に使用する。
 */

/** 分割後のセグメント */
export type InlineSegment =
  | { kind: 'text'; text: string }
  | { kind: 'token'; name: string; value: string };

/** `{{name:value}}`（value に `:` を含んでよい。最初の `:` のみ区切り） */
const TOKEN_RE = /\{\{\s*([A-Za-z][\w]*)\s*:([^}]*)\}\}/g;
/** `@userId` 糖衣（英数・ハイフン・アンダースコア・ドット） */
const MENTION_RE = /@([A-Za-z0-9._-]+)/g;

/**
 * text を、通常テキストとインライントークンのセグメント列に分割する。
 *
 * - `{{name:value}}` を token セグメントに。
 * - `@userId` は `{{mention:userId}}` と同義の token セグメントに変換。
 * - それ以外は text セグメント（Markdown はこの後段で解釈する想定）。
 *
 * @param text 対象テキスト
 * @param isKnownInline 種別がホワイトリスト登録済みかの判定。未登録トークンは text 扱い。
 */
export const splitInline = (
  text: string,
  isKnownInline: (name: string) => boolean,
): InlineSegment[] => {
  // まず {{...}} を処理し、残りテキストに対して @mention を処理する。
  const segments: InlineSegment[] = [];

  let lastIndex = 0;
  TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = TOKEN_RE.exec(text)) !== null) {
    const [full, name, rawValue] = m;
    const start = m.index;
    if (start > lastIndex) {
      pushWithMentions(segments, text.slice(lastIndex, start), isKnownInline);
    }
    if (isKnownInline(name)) {
      segments.push({ kind: 'token', name, value: rawValue.trim() });
    } else {
      // 未登録種別はそのまま文字列表示
      segments.push({ kind: 'text', text: full });
    }
    lastIndex = start + full.length;
  }
  if (lastIndex < text.length) {
    pushWithMentions(segments, text.slice(lastIndex), isKnownInline);
  }

  return segments;
};

/** テキスト断片に対して @mention を処理して push する */
const pushWithMentions = (
  segments: InlineSegment[],
  text: string,
  isKnownInline: (name: string) => boolean,
): void => {
  if (!isKnownInline('mention')) {
    if (text) segments.push({ kind: 'text', text });
    return;
  }
  let lastIndex = 0;
  MENTION_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MENTION_RE.exec(text)) !== null) {
    const [full, userId] = m;
    const start = m.index;
    if (start > lastIndex) {
      segments.push({ kind: 'text', text: text.slice(lastIndex, start) });
    }
    segments.push({ kind: 'token', name: 'mention', value: userId });
    lastIndex = start + full.length;
  }
  if (lastIndex < text.length) {
    segments.push({ kind: 'text', text: text.slice(lastIndex) });
  }
};
